import Api from '../../scripts/api';
import { ActionContext, ActionTree, MutationTree } from 'vuex';

enum MutationTypes {
    FETCHING = 'FETCHING',
    FETCHED = 'FETCHED',
}

export const STATES = {
    INIT: Symbol('INIT'),
    FETCHING: Symbol('FETCHING'),
    FETCHED: Symbol('FETCHED'),
    UNAVAILABLE: Symbol('UNAVAILABLE'),
};

type Discussion = Record<string, unknown>;
type State = {
    unreadPostsCount: null | number
    unreadDiscussions: Discussion[]
    unreadDiscussionsCount: null | number,
    state: symbol
};

type ApiData = {
    count: number,
    unreadDiscussions: Discussion[]
}

type MutationsWithoutParameter<S = State> = {
    [MutationTypes.FETCHING](state: S): void
}
type MutationsWithOneParameter<S = State> = {
    [MutationTypes.FETCHED](state: S, data: ApiData): void
}
// TODO check if this can be done easier (we need this to have correct typing in the commit() calls with different parameter sets)
type Mutations<S = State> = MutationsWithoutParameter & MutationsWithOneParameter;

const state: State = {
    unreadPostsCount: null,
    unreadDiscussions: [],
    unreadDiscussionsCount: null,
    state: STATES.INIT,
};

type AugmentedActionContext = {
    commit<K extends keyof MutationsWithoutParameter>(
        key: K
    ): ReturnType<MutationsWithoutParameter[K]>
} & {
    commit<K extends keyof MutationsWithOneParameter>(
        key: K,
        payload: Parameters<MutationsWithOneParameter[K]>[1]
    ): ReturnType<MutationsWithOneParameter[K]>
} & Omit<ActionContext<State, State>, 'commit'>

interface Actions {
    fetchCount({ commit }: AugmentedActionContext): Promise<void>
}

const actions: ActionTree<State, State> & Actions = {
    async fetchCount ({ commit, state }) {
        if (state.state !== STATES.INIT) {
            return;
        }

        commit(MutationTypes.FETCHING);

        // The Flarum discussion count
        // If no user is found in Flarum, we get back a simple message and the "data" key in the response (which our Api
        // abstraction automatically unwraps) is not present
        const result: ApiData | undefined = await Api.version(1).get<ApiData>('flarum/posts/unread');

        commit(MutationTypes.FETCHED, result);
    },
};

const getters = {
    unreadPostsCount (state: State): null | number {
        return state.unreadPostsCount;
    },

    unreadDiscussions (state: State): Discussion[] {
        return state.unreadDiscussions;
    },

    unreadDiscussionsCount (state: State): null | number {
        return state.unreadDiscussions?.length ?? 0;
    },

    state (state: State) {
        return state.state;
    },
};

const mutations: MutationTree<State> & Mutations = {
    FETCHING (state) {
        state.state = STATES.FETCHING;
    },

    FETCHED (state, data: ApiData | undefined) {
        if (data === undefined) {
            state.state = STATES.UNAVAILABLE;
            return;
        }
        state.state = STATES.FETCHED;
        state.unreadPostsCount = data.count;
        state.unreadDiscussions = data.unreadDiscussions;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
